import ko from 'knockout';
import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import styled from '@emotion/styled';

import { resetPassword } from '../../../main/scripts/utils/ApiUtilsV5';
import SectionBase from '../classes/sectionbase';
import Router from '../utils/router';
import { createPasswordValidationErrors } from '../../../main/scripts/utils/password';
import { useDocumentTitle } from '../../../main/scripts/utils/hooks';
import { Alert, Button, Field, Input } from '../../../main/scripts/lumen';
import { AuthCard, AuthHeading } from './login/AuthCard';

export default class ResetPasswordSection extends SectionBase {
  sectionRequiresAuth = false;

  constructor() {
    super();
    ReactDOM.render(<ResetPassword />, document.getElementById('sections'));
    ko.applyBindings(this);
  }
}

export const ResetPassword = () => {
  useDocumentTitle('Reset Password | Cemantica Insights hub');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState('');
  const [saving, setSaving] = useState(false);

  const onSubmit = event => {
    event.preventDefault();

    const token = Router.param('token');
    if (!token) {
      setErrorMessage('This is not a valid link to reset a password.');
      return;
    }

    const updatedErrors = createPasswordValidationErrors(
      password,
      confirmPassword,
      null
    );
    setErrors(updatedErrors);
    if (Object.keys(updatedErrors).length > 0) {
      return;
    }

    setErrorMessage('');
    setSaving(true);
    resetPassword(token, password)
      .then(() => {
        alert('Your password has been reset.');
        Router.go('login.html');
      })
      .catch(error => {
        setSaving(false);
        setErrorMessage(
          error.reason ? `${error.message}\n${error.reason}` : error.message
        );
      });
  };

  return (
    <AuthCard>
      <AuthHeading>Reset password</AuthHeading>
      <Intro>
        Resetting your password will log out all of your current sessions. If
        you have created a long-lived token for API use, it will <em>not</em> be
        removed.
      </Intro>
      {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
      <Form onSubmit={onSubmit}>
        <Field
          label="New password"
          htmlFor="reset-password"
          error={errors.newPass?.join('\n')}
        >
          <Input
            id="reset-password"
            type="password"
            autoComplete="new-password"
            required
            error={errors.newPass?.length > 0}
            value={password}
            onChange={event => setPassword(event.target.value)}
          />
        </Field>
        <Field
          label="Confirm new password"
          htmlFor="reset-confirm-password"
          error={errors.confirmPass?.join('\n')}
        >
          <Input
            id="reset-confirm-password"
            type="password"
            autoComplete="new-password"
            required
            error={errors.confirmPass?.length > 0}
            value={confirmPassword}
            onChange={event => setConfirmPassword(event.target.value)}
          />
        </Field>
        <Button
          type="submit"
          variant="primary"
          size="md"
          disabled={saving}
          aria-busy={saving}
          style={{ width: '100%' }}
        >
          {saving ? 'Setting password…' : 'Set password'}
        </Button>
      </Form>
    </AuthCard>
  );
};

const Intro = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.base};
  color: ${({ theme }) => theme.color.fg.muted};
  text-align: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;
